import { useState, useEffect, useCallback } from "react";
import { TrendingUp, RefreshCw, Pin } from "lucide-react";
import * as api from "../../lib/api";
import { fmt, pct } from "./shared";

function TrendSection({ title, color, icon, rows, columns }) {
  if (!rows.length) return null;
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm mb-6 overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <span style={{ color }} className="font-bold text-sm">{icon} {title}</span>
        <span className="text-xs text-zinc-400">({rows.length})</span>
      </div>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-zinc-50 border-b border-zinc-100">
            {columns.map(c => (
              <th key={c.key} className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400 whitespace-nowrap">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-zinc-50 hover:bg-zinc-50">
              {columns.map(c => (
                <td key={c.key} className="px-3 py-2 text-zinc-700">
                  {c.render ? c.render(row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const fmtRange = ([a, b] = []) => {
  const f = d => d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "—";
  return `${f(a)} – ${f(b)}`;
};

function Change({ value, invert = false, suffix = "%" }) {
  if (value == null) return <span className="text-zinc-300">—</span>;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? "#71717a" : good ? "#018D4C" : "#DC2626";
  return <span className="font-bold tabular-nums" style={{ color }}>{value > 0 ? "+" : ""}{value}{suffix}</span>;
}

const queryCol = { key: "query", label: "Query", render: r => <span className="font-medium text-zinc-900">{r.query}</span> };
const changeCols = [
  queryCol,
  { key: "impressions_before", label: "Prev Imp.", render: r => fmt(r.impressions_before) },
  { key: "impressions_after", label: "Now Imp.", render: r => fmt(r.impressions_after) },
  { key: "change_pct", label: "Change", render: r => <Change value={r.change_pct} /> },
  { key: "position", label: "Position", render: r => `${fmt(r.position_before, 1)} → ${fmt(r.position, 1)}` },
  { key: "clicks", label: "Clicks", render: r => `${fmt(r.clicks_before)} → ${fmt(r.clicks)}` },
];

export default function TrendsTab({ daysBack = 28 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  const load = useCallback((force = false) => {
    setLoading(true);
    setError(null);
    api.getGscTrends({ days_back: daysBack, ...(force ? { force: true } : {}) })
      .then(setData)
      .catch(e => setError(e?.response?.data?.detail || "Failed to load trends."))
      .finally(() => setLoading(false));
  }, [daysBack]);

  useEffect(() => { load(); }, [load]);

  function handlePushRising() {
    setPushing(true);
    setPushResult(null);
    api.pushRisingToRecommendations({ days_back: daysBack })
      .then(r => setPushResult(r))
      .catch(e => setPushResult({ error: e?.response?.data?.detail || "Failed to push." }))
      .finally(() => setPushing(false));
  }

  if (loading) return (
    <div className="flex items-center justify-center h-48 text-zinc-400 text-sm">
      Comparing periods… first load of the day fetches from Search Console.
    </div>
  );
  if (error) return (
    <div className="flex items-center justify-center h-48 text-red-500 text-sm">{error}</div>
  );

  const pinned = data.pinned || [];
  const empty = !pinned.length && !data.rising.length && !data.declining?.length
    && !data.quick_wins.length && !data.new_queries.length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-xs text-zinc-400">
          Last <strong>{data.days}d</strong> ({fmtRange(data.current_range)}) vs previous {data.days}d ({fmtRange(data.previous_range)})
          {" · "}{fmt(data.total_queries)} queries
          {data.clamped && (
            <span className="ml-2 text-amber-600">
              Capped at {data.days}d — GSC keeps ~16 months, not enough for two longer windows.
            </span>
          )}
          <div className="mt-0.5 text-[10px]">
            {data.cached ? "Cached" : "Fetched"} {data.computed_at ? new Date(data.computed_at).toLocaleString() : ""} · updates once a day
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pushResult && !pushResult.error && (
            <span className="text-xs text-emerald-600 font-medium">✓ {pushResult.added} added to Ideas</span>
          )}
          {pushResult?.error && <span className="text-xs text-red-500">{pushResult.error}</span>}
          {data.rising?.length > 0 && (
            <button
              onClick={handlePushRising}
              disabled={pushing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {pushing ? "Pushing…" : `Push ${data.rising.length} Rising → Ideas`}
            </button>
          )}
          <button
            onClick={() => load(true)}
            title="Refetch from Search Console now"
            className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {pinned.length > 0 ? (
        <TrendSection
          title="Pinned"
          color="#D97706"
          icon="📌"
          rows={pinned}
          columns={[
            { key: "value", label: "Query / Page", render: r => (
              <span className="font-medium text-zinc-900">
                <span className="mr-1.5 text-[9px] uppercase tracking-wide text-zinc-400">{r.kind}</span>
                {r.kind === "page" ? (r.value.replace(/^https?:\/\/[^/]+/, "") || "/") : r.value}
              </span>
            )},
            { key: "clicks", label: "Clicks", render: r => `${fmt(r.previous?.clicks)} → ${fmt(r.current?.clicks)}` },
            { key: "clicks_change_pct", label: "Δ Clicks", render: r => <Change value={r.clicks_change_pct} /> },
            { key: "impressions", label: "Impressions", render: r => `${fmt(r.previous?.impressions)} → ${fmt(r.current?.impressions)}` },
            { key: "impressions_change_pct", label: "Δ Imp.", render: r => <Change value={r.impressions_change_pct} /> },
            { key: "position", label: "Position", render: r => `${fmt(r.previous?.position, 1)} → ${fmt(r.current?.position, 1)}` },
            { key: "position_delta", label: "Δ Pos.", render: r => <Change value={r.position_delta} suffix="" /> },
          ]}
        />
      ) : (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-4 bg-amber-50/60 border border-amber-100 rounded-xl text-xs text-amber-800">
          <Pin size={12} /> Pin queries or pages in the Queries / Pages tabs to monitor them here period over period.
        </div>
      )}

      <TrendSection title="Rising Queries" color="#018D4C" icon="↑" rows={data.rising} columns={changeCols} />
      <TrendSection title="Declining Queries" color="#DC2626" icon="↓" rows={data.declining || []} columns={changeCols} />

      <TrendSection
        title="Quick Wins (pos 11–20)"
        color="#B45309"
        icon="⚡"
        rows={data.quick_wins}
        columns={[
          queryCol,
          { key: "position", label: "Position", render: r => (
            <span className="font-bold text-amber-700">{fmt(r.position, 1)}</span>
          )},
          { key: "impressions", label: "Impressions", render: r => fmt(r.impressions) },
          { key: "clicks", label: "Clicks", render: r => fmt(r.clicks) },
          { key: "ctr", label: "CTR", render: r => pct(r.ctr) },
        ]}
      />

      <TrendSection
        title="New Queries"
        color="#6D28D9"
        icon="✦"
        rows={data.new_queries}
        columns={[
          queryCol,
          { key: "impressions", label: "Impressions", render: r => fmt(r.impressions) },
          { key: "clicks", label: "Clicks", render: r => fmt(r.clicks) },
          { key: "ctr", label: "CTR", render: r => pct(r.ctr) },
          { key: "position", label: "Position", render: r => fmt(r.position, 1) },
        ]}
      />

      {empty && (
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <TrendingUp size={32} className="text-zinc-200" />
          <p className="text-zinc-500 text-sm">No significant changes between the two periods.</p>
        </div>
      )}
    </div>
  );
}
